import { computed, inject } from '@angular/core';
import { ApiQuery } from '@kinet-open-network/client/core/api-client';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withModel,
  withModelCrud,
  withPaginated,
  withQuery,
} from '@kinet-open-network/client/shared/data-access/state';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  signalStoreFeature,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, firstValueFrom, pipe, switchMap, tap } from 'rxjs';
import { IPairTransactionData } from '../models/api/pair-transaction.model';
import { BankAccount } from '../models/bank-account.model';
import { BankTransaction } from '../models/bank-transaction.model';
import { BankTransactionsService } from '../services/bank-transactions.service';
import { BanksService } from '../services/banks.service';

interface BankAccountsFeatureSliceState {
  bankAccounts: BankAccount[];
  bankAccountLoading: boolean;
  selectedBankAccount: BankAccount | null;
}

const withBankAccounts = () =>
  signalStoreFeature(
    withState<BankAccountsFeatureSliceState>({
      bankAccounts: [],
      bankAccountLoading: false,
      selectedBankAccount: null,
    }),
    withComputed((store) => ({
      selectedIban: computed(
        () => store.selectedBankAccount()?.iban || EMPTY_IBAN,
      ),
    })),
    withMethods((store, banksService = inject(BanksService)) => ({
      loadBankAccounts: async () => {
        patchState(store, { bankAccountLoading: true });

        try {
          const bankAccounts = await firstValueFrom(banksService.getAccounts());

          patchState(store, { bankAccounts });

          return bankAccounts;
        } finally {
          patchState(store, { bankAccountLoading: false });
        }
      },
      setSelectedBankAccount: (iban: string | null) => {
        patchState(store, {
          selectedBankAccount: iban
            ? store.bankAccounts().find((account) => account.iban === iban)
            : null,
        });
      },
    })),
  );

export const EMPTY_IBAN = 'none';

export const BankTransactionsStore = signalStore(
  { protectedState: false },
  withBankAccounts(),
  withQuery({
    initial: {
      sort: ['-paymentDate'],
    },
  }),
  withPaginated<BankTransaction>(),
  withCallState(),
  withModel<BankTransaction>(),
  withModelCrud(BankTransactionsService),
  withMethods(
    (store, transactionsService = inject(BankTransactionsService)) => ({
      loadBankTransactions: rxMethod<{
        iban?: string;
        query?: ApiQuery | null;
      }>(
        pipe(
          filter(({ iban }) => !!iban && iban !== EMPTY_IBAN),
          tap(() => {
            patchState(store, setLoading());
          }),
          switchMap(({ iban, query }) => {
            const mergedQuery = {
              ...query,
            };

            mergedQuery.filters = query?.filters
              ? `${query.filters} and (iban eq "${iban}")`
              : `iban eq "${iban}"`;

            return transactionsService.query(mergedQuery).pipe(
              tapResponse({
                next: (transactions) => {
                  patchState(store, { paginated: transactions });
                },
                error: (error) => {
                  patchState(store, setError(error));
                },
                complete: () => {
                  patchState(store, setLoaded());
                },
              }),
            );
          }),
        ),
      ),
      pairTransaction: async (
        transactionId: string,
        data: IPairTransactionData,
      ) => {
        patchState(store, setLoading());

        try {
          return firstValueFrom(
            transactionsService.pairTransaction(transactionId, data),
          );
        } catch (error) {
          patchState(store, setError(error));
          return null;
        } finally {
          patchState(store, setLoaded());
        }
      },
      autoPairTransaction: async (transactionId: string) => {
        patchState(store, setLoading());

        try {
          return firstValueFrom(
            transactionsService.autoPairTransaction(transactionId),
          );
        } catch (error) {
          patchState(store, setError(error));
          return null;
        } finally {
          patchState(store, setLoaded());
        }
      },
    }),
  ),
  withHooks({
    onInit: (store) => {
      const query = computed(() => ({
        iban: store.selectedIban(),
        query: store.query(),
      }));

      store.loadBankTransactions(query);
    },
  }),
  //     iban?: string, query?: ApiQuery | null) => {
  //     if (!iban || iban === EMPTY_IBAN) {
  //       return null;
  //     }

  //     try {
  //       const transactions = await firstValueFrom(
  //         banksService.getTransactions(iban, query),
  //       );

  //       patchState(store, { paginated: transactions });

  //       return transactions;
  //     } catch (error) {
  //       patchState(store, setError(error));
  //       return null;
  //     } finally {
  //       patchState(store, setLoaded());
  //     }
  //   },
  // })),
);
